import { useIntl } from "react-intl";

import Button from "@/components/wpl/button/src";
import {
    FeaturesTable,
    FeaturesTableHeader,
    FeaturesTableRow,
    FeaturesTableSection
} from "@/components/wpl/pricing/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import AtlassianGuardPremium from "@/templates/confluence-pricing-page/src/modal-contents/atlassian-guard-premium";

import AdminInsights from "../modal-contents/admin-insights";
import AdminKey from "../modal-contents/admin-key";
import Analytics from "../modal-contents/analytics";
import AnnouncementBanner from "../modal-contents/announcement-banner";
import AnonymousAccess from "../modal-contents/anonymous-access";
import ArchiveAndUnarchivePages from "../modal-contents/archive-and-unarchive-pages";
import AtlassianAnalytics from "../modal-contents/atlassian-anayltics";
import AtlassianDataLake from "../modal-contents/atlassian-data-lake";
import AtlassianGuardStandard from "../modal-contents/atlassian-guard-standard";
import AtlassianIntelligence from "../modal-contents/atlassian-intelligence";
import AuditLogs from "../modal-contents/audit-logs";
import AuditLogsForUserAcitivity from "../modal-contents/audit-logs-for-user-activity";
import Automation from "../modal-contents/automation";
import BulkArchive from "../modal-contents/bulk-archive";
import BusinessContinuityAndDisasterRecovery from "../modal-contents/business-continuity-and-disaster-recovery";
import BYOK from "../modal-contents/byok";
import CentralizedPerUserLicensing from "../modal-contents/centralized-user-licensing";
import CopySpacePermissions from "../modal-contents/copy-space-permissions";
import DataConnectors from "../modal-contents/data-connectors";
import DataResidency from "../modal-contents/data-residency";
import Databases from "../modal-contents/databases";
import DomainVerificationAndAccountCapture from "../modal-contents/domain-verification-and-account-capture";
import EmployeeDirectory from "../modal-contents/employee-directory";
import EncryptionInTransitAndAtRest from "../modal-contents/encryption-in-transit-and-rest";
import GoalsProjectsCommunication from "../modal-contents/goals-projects-communication";
import Guests from "../modal-contents/guests";
import InspectPermissions from "../modal-contents/inspect-permissions";
import IPAllowlisting from "../modal-contents/ip-allowlisting";
import Macros from "../modal-contents/macros";
import MobileDeviceManagement from "../modal-contents/mobile-device-management";
import MultipleIdP from "../modal-contents/multiple-idp";
import NumberOfSites from "../modal-contents/number-of-sites";
import PageInsights from "../modal-contents/page-insights";
import PageVersioning from "../modal-contents/page-versioning";
import PasswordPolicies from "../modal-contents/password-policies";
import ProductRequests from "../modal-contents/product-requests";
import PublicLinks from "../modal-contents/public-links";
import ReleaseTracks from "../modal-contents/release-tracks";
import Sandbox from "../modal-contents/sandbox";
import SessionDurationManagement from "../modal-contents/session-duration-management";
import SpaceAndPagePermissions from "../modal-contents/space-and-page-permissions";
import Storage from "../modal-contents/storage";
import StructuredPageTreeOrganization from "../modal-contents/structed-page-tree-organization";
import TeamCalendars from "../modal-contents/team-calendars";
import TemplateLibrary from "../modal-contents/template-library";
import UnlimitedSpacesAndPages from "../modal-contents/unlimited-spaces-and-pages";
import UserCounts from "../modal-contents/user-counts";
import UserLimitPerSite from "../modal-contents/user-limit-per-site";
import Whiteboards from "../modal-contents/whiteboards";

const tiers = ["Free", "Standard", "Premium", "Enterprise"];

const CloudFeaturesTables = () => {
    const intl = useIntl();
    const buttonText = intl.formatMessage({
        id: "ConfluencePricingPage.features.learnAboutFeatures",
        defaultMessage: "Learn about features"
    });

    return (
        <FeaturesTableSection>
            <FeaturesTableHeader>
                {intl.formatMessage({
                    id: "ConfluencePricingPage.featuresTableHeader",
                    defaultMessage: "Compare features"
                })}
            </FeaturesTableHeader>
            <CoreFeatures />
            <DataAndInsights />
            <AdminControls />
            <SecurityAndCompliance />
            <StorageSupportAndInvoicing />
            <ExternalCollaboration /> {/* This table does not always render */}
            <Button
                type="secondary"
                color="darkblue"
                text={buttonText}
                aria-label={buttonText}
                url={useLocalizedHref("/software/confluence/features")}
                dataEventProps={{
                    "data-event": "clicked",
                    "data-product-key": "confluence",
                    "data-event-component": "linkButton",
                    "data-event-container": "featuresSection",
                    "data-track-event": "cloudFeaturesTables.featuresButton"
                }}
            />
        </FeaturesTableSection>
    );
};

const CoreFeatures = () => {
    const intl = useIntl();

    const messages = {
        coreFeatures: intl.formatMessage({
            id: "ConfluencePricingPage.features.coreFeatures",
            defaultMessage: "Core Features"
        }),
        atlassianIntelligence: intl.formatMessage({
            id: "ConfluencePricingPage.features.atlassianIntelligence",
            defaultMessage: "Atlassian Intelligence (AI)"
        }),
        userLimit: intl.formatMessage({
            id: "ConfluencePricingPage.features.userLimit",
            defaultMessage: "User limit per site"
        }),
        users10: intl.formatMessage({
            id: "ConfluencePricingPage.features.userLimit.10",
            defaultMessage: "10 users"
        }),
        users50k: intl.formatMessage({
            id: "ConfluencePricingPage.features.userLimit.35k",
            defaultMessage: "50,000 users"
        }),
        numberOfSites: intl.formatMessage({
            id: "ConfluencePricingPage.features.numberOfSites",
            defaultMessage: "Number of sites"
        }),
        one: intl.formatMessage({
            id: "ConfluencePricingPage.features.numberOfSites.one",
            defaultMessage: "One"
        }),
        multiple: intl.formatMessage({
            id: "ConfluencePricingPage.features.numberOfSites.multiple",
            defaultMessage: "Up to 150"
        }),
        unlimitedSpacesAndPages: intl.formatMessage({
            id: "ConfluencePricingPage.features.unlimitedSpacesAndPages",
            defaultMessage: "Unlimited spaces and pages"
        }),
        whiteboards: intl.formatMessage({
            id: "ConfluencePricingPage.features.whiteboards",
            defaultMessage: "Whiteboards"
        }),
        beta: intl.formatMessage({
            id: "ConfluencePricingPage.features.beta",
            defaultMessage: "Beta"
        }),
        whiteboardsFree: intl.formatMessage({
            id: "ConfluencePricingPage.features.whiteboards.free",
            defaultMessage: "3 active whiteboards per user"
        }),
        whiteboardsStandard: intl.formatMessage({
            id: "ConfluencePricingPage.features.whiteboards.standard",
            defaultMessage: "3 active whiteboards per user"
        }),
        whiteboardsPremium: intl.formatMessage({
            id: "ConfluencePricingPage.features.whiteboards.premium",
            defaultMessage: "Unlimited"
        }),
        whiteboardsEnterprise: intl.formatMessage({
            id: "ConfluencePricingPage.features.whiteboards.enterprise",
            defaultMessage: "Unlimited"
        }),
        databases: intl.formatMessage({
            id: "ConfluencePricingPage.features.databases",
            defaultMessage: "Databases"
        }),
        comingSoon: intl.formatMessage({
            id: "ConfluencePricingPage.features.comingSoon",
            defaultMessage: "Beta"
        }),
        macros: intl.formatMessage({
            id: "ConfluencePricingPage.features.macros",
            defaultMessage: "Macros"
        }),
        structuredPageTreeOrganization: intl.formatMessage({
            id: "ConfluencePricingPage.features.structuredPageTreeOrganization",
            defaultMessage: "Structured page tree organization"
        }),
        pageVersioning: intl.formatMessage({
            id: "ConfluencePricingPage.features.pageVersioning",
            defaultMessage: "Page versioning"
        }),
        templateLibrary: intl.formatMessage({
            id: "ConfluencePricingPage.features.templateLibrary",
            defaultMessage: "Template library"
        }),
        appsAndIntegrations: intl.formatMessage({
            id: "ConfluencePricingPage.features.appsAndIntegrations",
            defaultMessage: "Apps and integrations"
        }),
        goalsProjectsCommunication: intl.formatMessage({
            id: "ConfluencePricingPageTeam24.features.goalsProjectsCommunication.title",
            defaultMessage: "Goals, projects, and communication"
        }),
        employeeDirectory: intl.formatMessage({
            id: "ConfluencePricingPageTeam24.features.employeeDirectory.title",
            defaultMessage: "Employee directory for people and teams"
        }),
        spaceAndPagePermissions: intl.formatMessage({
            id: "ConfluencePricingPage.features.spaceAndPagePermissions",
            defaultMessage: "Space and page permissions"
        }),
        archiveAndUnarchivePages: intl.formatMessage({
            id: "ConfluencePricingPage.features.archiveAndUnarchivePages",
            defaultMessage: "Archive and unarchive pages"
        }),
        pageInsights: intl.formatMessage({
            id: "ConfluencePricingPage.features.pageInsights",
            defaultMessage: "Page insights"
        }),
        externalCollaboration: intl.formatMessage({
            id: "ConfluencePricingPage.features.externalCollaborationGuests",
            defaultMessage: "External collaboration: Guests"
        }),
        analytics: intl.formatMessage({
            id: "ConfluencePricingPage.features.analytics",
            defaultMessage: "Analytics"
        }),
        inspectPermissions: intl.formatMessage({
            id: "ConfluencePricingPage.features.inspectPermissions",
            defaultMessage: "Inspect permissions"
        }),
        bulkArchive: intl.formatMessage({
            id: "ConfluencePricingPage.features.bulkArchive",
            defaultMessage: "Bulk archive"
        }),
        teamCalendars: intl.formatMessage({
            id: "ConfluencePricingPage.features.teamCalendars",
            defaultMessage: "Team calendars"
        }),
        automation: intl.formatMessage({
            id: "ConfluencePricingPage.features.automation",
            defaultMessage: "Automation"
        })
    };

    return (
        <FeaturesTable captionText={messages.coreFeatures} planTiers={tiers}>
            {/* This first row (Ai) does not always render on prod */}
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianIntelligence,
                    content: AtlassianIntelligence
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.userLimit,
                    content: UserLimitPerSite
                }}
                tierCellsContent={[
                    messages.users10,
                    messages.users50k,
                    messages.users50k,
                    messages.users50k
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.numberOfSites,
                    content: NumberOfSites
                }}
                tierCellsContent={[
                    messages.one,
                    messages.one,
                    messages.one,
                    messages.multiple
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.unlimitedSpacesAndPages,
                    content: UnlimitedSpacesAndPages
                }}
                tierCellsContent={[true, true, true, true]}
            />

            <FeaturesTableRow
                featureCellContent={{
                    text: messages.whiteboards,
                    content: Whiteboards
                }}
                tierCellsContent={[
                    messages.whiteboardsFree,
                    messages.whiteboardsStandard,
                    messages.whiteboardsPremium,
                    messages.whiteboardsEnterprise
                ]}
            />

            <FeaturesTableRow
                featureCellContent={{
                    text: messages.databases,
                    content: Databases
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.macros,
                    content: Macros
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.structuredPageTreeOrganization,
                    content: StructuredPageTreeOrganization
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.pageVersioning,
                    content: PageVersioning
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.templateLibrary,
                    content: TemplateLibrary
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    type: "external",
                    href: "https://marketplace.atlassian.com/search?product=confluence",
                    text: messages.appsAndIntegrations
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.goalsProjectsCommunication,
                    content: GoalsProjectsCommunication
                }}
                tierCellsContent={[true, true, true, true]}
                lozengeText={messages.beta}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.employeeDirectory,
                    content: EmployeeDirectory
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.spaceAndPagePermissions,
                    content: SpaceAndPagePermissions
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.archiveAndUnarchivePages,
                    content: ArchiveAndUnarchivePages
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.pageInsights,
                    content: PageInsights
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.analytics,
                    content: Analytics
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.inspectPermissions,
                    content: InspectPermissions
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.bulkArchive,
                    content: BulkArchive
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.teamCalendars,
                    content: TeamCalendars
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.automation,
                    content: Automation
                }}
                tierCellsContent={[false, false, true, true]}
            />
        </FeaturesTable>
    );
};

const DataAndInsights = () => {
    const intl = useIntl();

    const messages = {
        dataAndInsights: intl.formatMessage({
            id: "ConfluencePricingPage.features.DataAndInsights",
            defaultMessage: "Data & insights"
        }),
        atlassianAnalytics: intl.formatMessage({
            id: "ConfluencePricingPage.features.atlassianAnalytics",
            defaultMessage: "Atlassian Analytics"
        }),
        atlassianDataLake: intl.formatMessage({
            id: "ConfluencePricingPage.features.atlassianDataLake",
            defaultMessage: "Atlassian Data Lake"
        }),
        dataConnectors: intl.formatMessage({
            id: "ConfluencePricingPage.features.dataConnectors",
            defaultMessage: "Data connectors"
        })
    };

    return (
        <FeaturesTable captionText={messages.dataAndInsights} planTiers={tiers}>
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianAnalytics,
                    content: AtlassianAnalytics
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianDataLake,
                    content: AtlassianDataLake
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.dataConnectors,
                    content: DataConnectors
                }}
                tierCellsContent={[false, false, false, true]}
            />
        </FeaturesTable>
    );
};

const AdminControls = () => {
    const intl = useIntl();

    const messages = {
        adminControls: intl.formatMessage({
            id: "ConfluencePricingPage.features.adminControls",
            defaultMessage: "Admin controls"
        }),
        domainVerificationAndAccountCapture: intl.formatMessage({
            id: "ConfluencePricingPage.features.domainVerificationAndAccountCapture",
            defaultMessage: "Domain verification and account capture"
        }),
        sessionDurationManagement: intl.formatMessage({
            id: "ConfluencePricingPage.features.sessionDurationManagement",
            defaultMessage: "Session duration management (desktop)"
        }),
        adminKey: intl.formatMessage({
            id: "ConfluencePricingPage.features.adminKey",
            defaultMessage: "Admin key"
        }),
        announcementBanner: intl.formatMessage({
            id: "ConfluencePricingPage.features.announcementBanner",
            defaultMessage: "Announcement banner"
        }),
        copySpacePermissions: intl.formatMessage({
            id: "ConfluencePricingPage.features.copySpacePermissions",
            defaultMessage: "Copy space permissions"
        }),
        adminInsights: intl.formatMessage({
            id: "ConfluencePricingPage.features.adminInsights",
            defaultMessage: "Admin insights"
        }),
        sandbox: intl.formatMessage({
            id: "ConfluencePricingPage.features.sandbox",
            defaultMessage: "Sandbox"
        }),
        releaseTracks: intl.formatMessage({
            id: "ConfluencePricingPage.features.releaseTracks",
            defaultMessage: "Release tracks"
        }),
        userCounts: intl.formatMessage({
            id: "ConfluencePricingPage.features.userCounts",
            defaultMessage: "User counts"
        }),
        productRequests: intl.formatMessage({
            id: "ConfluencePricingPage.features.productRequests",
            defaultMessage: "Product requests"
        })
    };

    return (
        <FeaturesTable captionText={messages.adminControls} planTiers={tiers}>
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.domainVerificationAndAccountCapture,
                    content: DomainVerificationAndAccountCapture
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.sessionDurationManagement,
                    content: SessionDurationManagement
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.adminKey,
                    content: AdminKey
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.announcementBanner,
                    content: AnnouncementBanner
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.copySpacePermissions,
                    content: CopySpacePermissions
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.adminInsights,
                    content: AdminInsights
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.sandbox,
                    content: Sandbox
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.releaseTracks,
                    content: ReleaseTracks
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.productRequests,
                    content: ProductRequests
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.userCounts,
                    content: UserCounts
                }}
                tierCellsContent={[false, false, false, true]}
            />
        </FeaturesTable>
    );
};

const SecurityAndCompliance = () => {
    const intl = useIntl();

    const messages = {
        securityAndCompliance: intl.formatMessage({
            id: "ConfluencePricingPage.features.securityAndCompliance",
            defaultMessage: "Security & Compliance"
        }),
        atlassianGuardStandard: intl.formatMessage({
            id: "ConfluencePricingPageTeam24.features.atlassianGuardStandard.title",
            defaultMessage:
                "Atlassian Guard Standard (SSO, SCIM, mobile app management)"
        }),
        requiresAtlassianGuardStandardSubscription: intl.formatMessage({
            id: "ConfluencePricingPage.features.requiresAtlassianGuardStandardSubscription",
            defaultMessage: "Requires Atlassian Guard Standard subscription"
        }),
        atlassianGuardPremium: intl.formatMessage({
            id: "ConfluencePricingPageTeam24.features.atlassianGuardPremium.title",
            defaultMessage:
                "Atlassian Guard Premium (Data classification, threat detections, content scanning)"
        }),
        requiresAtlassianGuardPremiumSubscription: intl.formatMessage({
            id: "ConfluencePricingPage.features.requiresAtlassianGuardPremiumSubscription",
            defaultMessage: "Requires Atlassian Guard Premium subscription"
        }),
        included: intl.formatMessage({
            id: "ConfluencePricingPage.features.included",
            defaultMessage: "Included"
        }),
        passwordPolicies: intl.formatMessage({
            id: "ConfluencePricingPage.features.passwordPolicies",
            defaultMessage: "Password policies"
        }),
        encryptionInTransitAndAtRest: intl.formatMessage({
            id: "ConfluencePricingPage.features.encryptionInTransitAndAtRest",
            defaultMessage: "Encryption in transit and at rest"
        }),
        businessContinuityAndDisasterRecovery: intl.formatMessage({
            id: "ConfluencePricingPage.features.businessContinuityAndDisasterRecovery",
            defaultMessage: "Business continuity and disaster recovery"
        }),
        mdm: intl.formatMessage({
            id: "ConfluencePricingPage.features.mdm",
            defaultMessage: "MDM (Mobile Device Management)"
        }),
        auditLogs: intl.formatMessage({
            id: "ConfluencePricingPage.features.auditLogs",
            defaultMessage: "Audit logs"
        }),
        anonymousAccess: intl.formatMessage({
            id: "ConfluencePricingPage.features.anonymousAccess",
            defaultMessage: "Anonymous access"
        }),
        dataResidency: intl.formatMessage({
            id: "ConfluencePricingPage.features.dataResidency",
            defaultMessage: "Data residency"
        }),
        ipAllowlisting: intl.formatMessage({
            id: "ConfluencePricingPage.features.ipAllowlisting",
            defaultMessage: "IP allowlisting"
        }),
        multipleIdP: intl.formatMessage({
            id: "ConfluencePricingPage.features.multipleIdP",
            defaultMessage: "Multiple idP"
        }),
        auditLogsForUserActivity: intl.formatMessage({
            id: "ConfluencePricingPage.features.auditLogsForUserActivity",
            defaultMessage: "Audit logs for user activity"
        }),
        byok: intl.formatMessage({
            id: "ConfluencePricingPage.features.byok",
            defaultMessage: "BYOK"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.securityAndCompliance}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianGuardStandard,
                    content: AtlassianGuardStandard
                }}
                tierCellsContent={[
                    messages.requiresAtlassianGuardStandardSubscription,
                    messages.requiresAtlassianGuardStandardSubscription,
                    messages.requiresAtlassianGuardStandardSubscription,
                    messages.included
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.atlassianGuardPremium,
                    content: AtlassianGuardPremium
                }}
                tierCellsContent={[
                    messages.requiresAtlassianGuardPremiumSubscription,
                    messages.requiresAtlassianGuardPremiumSubscription,
                    messages.requiresAtlassianGuardPremiumSubscription,
                    messages.requiresAtlassianGuardPremiumSubscription
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.passwordPolicies,
                    content: PasswordPolicies
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.encryptionInTransitAndAtRest,
                    content: EncryptionInTransitAndAtRest
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.businessContinuityAndDisasterRecovery,
                    content: BusinessContinuityAndDisasterRecovery
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.mdm,
                    content: MobileDeviceManagement
                }}
                tierCellsContent={[true, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.auditLogs,
                    content: AuditLogs
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.dataResidency,
                    content: DataResidency
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.ipAllowlisting,
                    content: IPAllowlisting
                }}
                tierCellsContent={[false, false, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.multipleIdP,
                    content: MultipleIdP
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.auditLogsForUserActivity,
                    content: AuditLogsForUserAcitivity
                }}
                tierCellsContent={[false, false, false, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.byok,
                    content: BYOK
                }}
                tierCellsContent={[false, false, false, true]}
            />
        </FeaturesTable>
    );
};

const StorageSupportAndInvoicing = () => {
    const intl = useIntl();

    const messages = {
        storageSupportAndInvoicing: intl.formatMessage({
            id: "ConfluencePricingPage.features.storageSupportAndInvoicing",
            defaultMessage: "Storage, Support & Invoicing"
        }),
        fileStorate2GB: intl.formatMessage({
            id: "ConfluencePricingPage.features.fileStorate2GB",
            defaultMessage: "2 GB file storage"
        }),
        fileStorate250GB: intl.formatMessage({
            id: "ConfluencePricingPage.features.fileStorate250GB",
            defaultMessage: "250 GB file storage"
        }),
        fileStorateUnlimited: intl.formatMessage({
            id: "ConfluencePricingPage.features.fileStorateUnlimited",
            defaultMessage: "Unlimited file storage"
        }),
        support: intl.formatMessage({
            id: "ConfluencePricingPage.features.support",
            defaultMessage: "Support"
        }),
        communitySupport: intl.formatMessage({
            id: "ConfluencePricingPage.features.communitySupport",
            defaultMessage: "Community Support"
        }),
        localBusinessHours: intl.formatMessage({
            id: "ConfluencePricingPage.features.localBusinessHours",
            defaultMessage: "Local Business Hours"
        }),
        premiumSupport: intl.formatMessage({
            id: "ConfluencePricingPage.features.premiumSupport",
            defaultMessage: "24/7 Premium Support"
        }),
        enterpriseSupport: intl.formatMessage({
            id: "ConfluencePricingPage.features.enterpriseSupport",
            defaultMessage: "24/7 Enterprise Support"
        }),
        guaranteedUptimeSLA: intl.formatMessage({
            id: "ConfluencePricingPage.features.guaranteedUptimeSLA",
            defaultMessage: "Guaranteed uptime SLA"
        }),
        percent999: intl.formatMessage({
            id: "ConfluencePricingPage.features.percent999",
            defaultMessage: "99.9%"
        }),
        percent9995: intl.formatMessage({
            id: "ConfluencePricingPage.features.percent9995",
            defaultMessage: "99.95%"
        }),
        centralizedPerUserLicensing: intl.formatMessage({
            id: "ConfluencePricingPage.features.centralizedPerUserLicensing",
            defaultMessage: "Centralized per-user licensing"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.storageSupportAndInvoicing}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: "Storage",
                    content: Storage
                }}
                tierCellsContent={[
                    messages.fileStorate2GB,
                    messages.fileStorate250GB,
                    messages.fileStorateUnlimited,
                    messages.fileStorateUnlimited
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    type: "external",
                    href: useLocalizedHref("/enterprise/support-services"),
                    text: messages.support
                }}
                tierCellsContent={[
                    messages.communitySupport,
                    messages.localBusinessHours,
                    messages.premiumSupport,
                    messages.enterpriseSupport
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    type: "external",
                    href: useLocalizedHref("/legal/sla"),
                    text: messages.guaranteedUptimeSLA
                }}
                tierCellsContent={[
                    false,
                    false,
                    messages.percent999,
                    messages.percent9995
                ]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.centralizedPerUserLicensing,
                    content: CentralizedPerUserLicensing
                }}
                tierCellsContent={[false, false, false, true]}
            />
        </FeaturesTable>
    );
};

const ExternalCollaboration = () => {
    const intl = useIntl();

    const messages = {
        externalCollaboration: intl.formatMessage({
            id: "ConfluencePricingPage.features.externalCollaboration",
            defaultMessage: "External Collaboration"
        }),
        publicLinks: intl.formatMessage({
            id: "ConfluencePricingPage.features.publicLinks",
            defaultMessage: "Public links"
        }),
        guests: intl.formatMessage({
            id: "ConfluencePricingPage.features.guests",
            defaultMessage: "Guests"
        }),
        anonymousAccess: intl.formatMessage({
            id: "ConfluencePricingPage.features.anonymousAccess",
            defaultMessage: "Anonymous access"
        })
    };

    return (
        <FeaturesTable
            captionText={messages.externalCollaboration}
            planTiers={tiers}
        >
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.publicLinks,
                    content: PublicLinks
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.guests,
                    content: Guests
                }}
                tierCellsContent={[false, true, true, true]}
            />
            <FeaturesTableRow
                featureCellContent={{
                    text: messages.anonymousAccess,
                    content: AnonymousAccess
                }}
                tierCellsContent={[false, true, true, true]}
            />
        </FeaturesTable>
    );
};

export default CloudFeaturesTables;
